import React from 'react';
import PropTypes from 'prop-types';
import { INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

import { useLocale } from '$contexts/locale';
import { resolvePath } from '$utilities/routes';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @component RichText
 * @props (see propTypes)
 */
const RichText = ({ domain, json, links, ...remainingProps }) => {
    const { formatURI } = useLocale();

    if (!json) {
        return null;
    }

    const richTextOptions = {
        renderText: (text) => {
            return text.split('\n').flatMap((text, i) => [i > 0 && <br key={i} />, text]);
        },
        renderNode: {
            [INLINES.HYPERLINK]: (node, children) => {
                const href = typeof node.data.uri === 'string' ? node.data.uri : '';
                const localLink = (domain ? href.includes(domain) : false) || href.startsWith('/') || href.startsWith('#');
                return (
                    <a
                        href={formatURI(href)}
                        rel={localLink ? '' : 'noreferrer noopener nofollow'}
                        target={localLink ? '' : '_blank'}>
                        {children}
                    </a>
                );
            },
            [INLINES.ENTRY_HYPERLINK]: (node, children) => {
                const matchingEntity = links?.entries.hyperlink
                    .filter(page => page?.__typename && page?.sys && page?.cfMetadata)
                    .find(({ sys }) => sys?.id === node.data.target.sys.id);
                if (matchingEntity) {
                    const pageUrl = formatURI(resolvePath({ cfMetadata: matchingEntity.cfMetadata, slug: matchingEntity.slug  }));
                    return pageUrl ? <a href={pageUrl}>{children}</a> : children;
                } else {
                    return children;
                }
            }
        }
    };

    return (
        <div {...remainingProps}>
            {documentToReactComponents(json, richTextOptions)}
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    domain: PropTypes.string,
    json: PropTypes.object,
    links: PropTypes.object
};

RichText.propTypes = propTypes;

export default RichText;
